/**
 * Advertisement / Banner API layer. All banner-related API calls go through this service.
 * Components and hooks must not call requestWithBaseUrl or fetch directly for adv endpoints.
 */
import { requestWithBaseUrl, requestWithBaseUrlPublic } from '../api/request';
import { Endpoints } from '../api';
import type {
    HomeAdItem,
    UserBannersResponse,
    AdminBannersResponse,
    CreateBannerOrderPayload,
    BannerOrderResponse,
    BannerOrderResponseRaw
} from '../types/banner';
import type { BannerPricesResponse, CreateBannerResponse } from '../types/advertisement';

const BANNER_STATUS = { pending: 1, approved: 2, rejected: 3 } as const;

/** GET /adv/GetHomeAdv – home page carousel ads (public, no login required) */
export async function getHomeAds(): Promise<HomeAdItem[] | null> {
    const res = await requestWithBaseUrlPublic<HomeAdItem[]>(Endpoints.getAdsItems, { method: 'GET' });
    return Array.isArray(res) ? res : null;
}

/** GET /adv/GetBannerByUser/:userId */
export async function getUserBanners(userId: number): Promise<UserBannersResponse | null> {
    return requestWithBaseUrl<UserBannersResponse>(`${Endpoints.getUserBanners}/${userId}`, {
        method: 'GET'
    });
}

/** GET /adv/GetHomeBanners – admin list */
export async function getHomeBanners(): Promise<AdminBannersResponse | null> {
    return requestWithBaseUrl<AdminBannersResponse>(Endpoints.getHomeBanners, { method: 'GET' });
}

/** POST /adv/Create – multipart FormData */
export async function createBanner(formData: FormData): Promise<CreateBannerResponse | null> {
    return requestWithBaseUrl<CreateBannerResponse | null>(Endpoints.createAdv, {
        method: 'POST',
        body: formData
    });
}

/** POST /adv/Order */
export async function createBannerOrder(
    payload: CreateBannerOrderPayload & { userId: number }
): Promise<BannerOrderResponse | null> {
    const res = await requestWithBaseUrl<BannerOrderResponseRaw | null>(Endpoints.createBannerOrder, {
        method: 'POST',
        body: JSON.stringify({
            userId: payload.userId,
            bannerId: payload.bannerId,
            paymentMethodId: payload.paymentMethodId
        })
    });
    if (!res || typeof res !== 'object') return null;
    const orderId = Number(res.orderId ?? res.OrderId);
    const amount = Number(res.convertedTotalAmount ?? res.ConvertedTotalAmount);
    const paymentMethodId = Number(res.paymentMethodId ?? res.PaymentMethodId);
    const userId = Number(res.userId ?? res.UserId) ?? payload.userId;
    const orderStatus = String(res.orderStatus ?? res.OrderStatus ?? 'pending');
    if (!Number.isFinite(orderId) || !Number.isFinite(amount)) return null;
    return {
        orderId,
        userId,
        orderStatus,
        convertedTotalAmount: amount,
        paymentMethodId: Number.isFinite(paymentMethodId) ? paymentMethodId : 0
    };
}

/** POST /adv/Approve */
export async function approveBanner(bannerId: number): Promise<void> {
    await requestWithBaseUrl(Endpoints.approveBanner, {
        method: 'POST',
        body: JSON.stringify({ bannerId, statusId: BANNER_STATUS.approved })
    });
}

/** POST /adv/Reject */
export async function rejectBanner(bannerId: number): Promise<void> {
    await requestWithBaseUrl(Endpoints.rejectBanner, {
        method: 'POST',
        body: JSON.stringify({ bannerId, statusId: BANNER_STATUS.rejected })
    });
}

/** GET /adv/Prices */
export async function getBannerPrices(): Promise<BannerPricesResponse | null> {
    return requestWithBaseUrl<BannerPricesResponse>(Endpoints.getAdvPrices, { method: 'GET' });
}
